import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  SquaresFour,
  Folder,
  Flag,
  Lifebuoy,
  UsersThree,
  CaretDoubleLeft,
  CaretDoubleRight,
  Plus,
} from "@phosphor-icons/react";
import { BrandmarkHorizontal } from "@/components/Brandmark";

// Dashboard-scoped left sidebar. Mirrors the in-project sidebar but for the
// folder-view (Dashboard). Defaults expanded. Includes branding at the top,
// nav items in the middle, a `+ New Project` CTA above the user chip, and the
// user chip at the very bottom.
export default function DashboardSidebar({
  collapsed,
  onToggleCollapsed,
  user,
  onNewProject,
  onOpenMilestones,
  onOpenSupport,
  activeItem = "dashboard",
}) {
  const items = [
    { id: "dashboard", label: "Dashboard", icon: SquaresFour, href: "/dashboard" },
    { id: "projects", label: "Projects", icon: Folder, href: "/dashboard?view=projects" },
    { id: "milestones", label: "Milestones", icon: Flag, onClick: onOpenMilestones },
    { id: "support", label: "Support", icon: Lifebuoy, onClick: onOpenSupport },
    { id: "standup", label: "Stand Up", icon: UsersThree, href: "/standup" },
  ];

  return (
    <aside
      className={`shrink-0 border-r border-slate-200 bg-white flex flex-col transition-[width] duration-150 sticky top-0 h-screen ${
        collapsed ? "w-16" : "w-60"
      }`}
      data-testid="dashboard-sidebar"
    >
      {/* Brand */}
      <div className="h-16 flex items-center px-3 border-b border-slate-200">
        {collapsed ? (
          <Link to="/" className="flex items-center justify-center w-full">
            <img src="/favicon.svg" alt="ThatGantt" className="h-8 w-8 rounded-md object-contain" />
          </Link>
        ) : (
          <Link to="/" className="flex items-center pl-1">
            <BrandmarkHorizontal height={28} />
          </Link>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 space-y-1 px-2">
        {items.map((it) => {
          const Icon = it.icon;
          const isActive = it.id === activeItem;
          const baseCls =
            "group flex items-center gap-3 h-10 rounded-md transition-colors w-full text-left";
          const stateCls = isActive
            ? "bg-slate-950 text-white"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-950";
          const inner = (
            <>
              <span className="h-10 w-10 flex items-center justify-center shrink-0">
                <Icon size={18} weight="bold" />
              </span>
              {!collapsed && (
                <span className="text-sm font-medium tracking-tight truncate">{it.label}</span>
              )}
            </>
          );
          if (it.href) {
            return (
              <Link
                key={it.id}
                to={it.href}
                className={`${baseCls} ${stateCls}`}
                title={collapsed ? it.label : undefined}
                data-testid={`dashboard-sidebar-${it.id}`}
              >
                {inner}
              </Link>
            );
          }
          return (
            <button
              key={it.id}
              type="button"
              onClick={it.onClick}
              className={`${baseCls} ${stateCls}`}
              title={collapsed ? it.label : undefined}
              data-testid={`dashboard-sidebar-${it.id}`}
            >
              {inner}
            </button>
          );
        })}
      </nav>

      {/* New Project CTA */}
      <div className="px-2 pb-2">
        <Button
          onClick={onNewProject}
          className={`w-full h-10 rounded-md text-white font-medium ${
            collapsed ? "px-0" : "px-3 justify-start"
          }`}
          style={{ background: "#4f56ff" }}
          data-testid="dashboard-sidebar-new-project"
          title={collapsed ? "New project" : undefined}
        >
          <Plus size={14} weight="bold" />
          {!collapsed && <span className="ml-2">New Project</span>}
        </Button>
      </div>

      {/* User chip */}
      {user && (
        <div className="p-2 border-t border-slate-200">
          <div className={`flex items-center gap-2 ${collapsed ? "justify-center" : "px-1"}`} data-testid="dashboard-sidebar-user">
            <span
              className="h-8 w-8 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-xs font-semibold shrink-0"
              title={user.email}
            >
              {(user.name || user.email || "?").charAt(0).toUpperCase()}
            </span>
            {!collapsed && (
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">
                  {user.name || user.email?.split("@")[0]}
                </div>
                <div className="text-[10px] text-slate-500 truncate uppercase tracking-widest font-sf-label">
                  {user.tier || "Free"} workspace
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Collapse toggle */}
      <div className="border-t border-slate-200 p-2">
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="w-full h-9 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-950 flex items-center justify-center gap-2 transition-colors"
          data-testid="dashboard-sidebar-toggle"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <CaretDoubleRight size={14} weight="bold" /> : (
            <>
              <CaretDoubleLeft size={14} weight="bold" />
              <span className="text-xs font-medium">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
